import { ThemeRecord, ThemeResponse } from './pocketbase.types'

export const THEME_RECORD_ID = 'thmebase0000001'

export const DEFAULT_THEME: ThemeRecord = {
  id: THEME_RECORD_ID,
  primary: '#832BBA',
  background: '#150C3A',
  border: '#2A1E68',
  text: '#ffffff',
  text_secondary: '#A7A0C8',
}

const HEX6_RE = /^#?[0-9a-fA-F]{6}$/

export function isValidHex(color: string): boolean {
  return HEX6_RE.test(color)
}

export function normalizeHex(color: string): string {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(color.trim())
  return m ? '#' + m[1].toLowerCase() : color
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#?([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/.exec(hex)
  if (!m) return null
  return { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) }
}

function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) => Math.round(n).toString(16).padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

function lighten(hex: string, amount: number): string {
  const rgb = hexToRgb(hex)
  if (!rgb) return hex
  return rgbToHex(
    rgb.r + (255 - rgb.r) * amount,
    rgb.g + (255 - rgb.g) * amount,
    rgb.b + (255 - rgb.b) * amount,
  )
}

function darken(hex: string, amount: number): string {
  const rgb = hexToRgb(hex)
  if (!rgb) return hex
  return rgbToHex(
    rgb.r * (1 - amount),
    rgb.g * (1 - amount),
    rgb.b * (1 - amount),
  )
}

function hexToRgbTuple(hex: string): string | null {
  const rgb = hexToRgb(hex)
  if (!rgb) return null
  return `${rgb.r}, ${rgb.g}, ${rgb.b}`
}

function isLight(hex: string): boolean {
  const rgb = hexToRgb(hex)
  if (!rgb) return false
  return (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000 > 128
}

export function deriveThemeValues(theme: { primary: string; background: string; border: string; text: string; text_secondary: string }) {
  const primaryContrast = isLight(theme.primary) ? '#000000' : '#ffffff'
  const p = theme.primary
  return {
    '--theme-primary': p,
    '--theme-primary-rgb': hexToRgbTuple(p) || '131, 43, 186',
    '--theme-primary-contrast': primaryContrast,
    '--theme-primary-hover': lighten(p, 0.65),
    '--theme-primary-active': lighten(p, 0.8),
    '--theme-primary-50': lighten(p, 0.95),
    '--theme-primary-100': lighten(p, 0.8),
    '--theme-primary-200': lighten(p, 0.6),
    '--theme-primary-300': lighten(p, 0.35),
    '--theme-primary-400': lighten(p, 0.15),
    '--theme-primary-500': p,
    '--theme-primary-600': darken(p, 0.15),
    '--theme-primary-700': darken(p, 0.3),
    '--theme-primary-800': darken(p, 0.5),
    '--theme-primary-900': darken(p, 0.7),
    '--primary-50': lighten(p, 0.95),
    '--primary-100': lighten(p, 0.8),
    '--primary-200': lighten(p, 0.6),
    '--primary-300': lighten(p, 0.35),
    '--primary-400': lighten(p, 0.15),
    '--primary-500': p,
    '--primary-600': darken(p, 0.15),
    '--primary-700': darken(p, 0.3),
    '--primary-800': darken(p, 0.5),
    '--primary-900': darken(p, 0.7),
    '--theme-bg': theme.background,
    '--theme-bg-ground': darken(theme.background, 0.15),
    '--theme-bg-overlay': lighten(theme.background, 0.08),
    '--theme-border': theme.border,
    '--theme-text': theme.text,
    '--theme-text-secondary': theme.text_secondary,
    '--primary-color': p,
    '--primary-color-text': primaryContrast,
    '--text-color': theme.text,
    '--text-color-secondary': theme.text_secondary,
    '--surface-0': theme.background,
    '--surface-100': theme.border,
    '--surface-ground': darken(theme.background, 0.15),
    '--surface-section': theme.background,
    '--surface-card': theme.background,
    '--surface-overlay': lighten(theme.background, 0.08),
    '--surface-border': theme.border,
    '--highlight-bg': `rgba(${hexToRgbTuple(p)}, 0.16)`,
  }
}

export function applyTheme(vars: Record<string, string | null>) {
  const root = document.documentElement
  for (const [key, value] of Object.entries(vars)) {
    if (value !== null) {
      root.style.setProperty(key, value)
    }
  }
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) {
    meta.setAttribute('content', vars['--theme-bg'] || '#150C3A')
  }
}

export function resetThemeVariables() {
  const root = document.documentElement
  const themeVars = [
    '--theme-primary', '--theme-primary-rgb', '--theme-primary-contrast',
    '--theme-primary-hover', '--theme-primary-active',
    '--theme-primary-50', '--theme-primary-100', '--theme-primary-200',
    '--theme-primary-300', '--theme-primary-400', '--theme-primary-500',
    '--theme-primary-600', '--theme-primary-700', '--theme-primary-800',
    '--theme-primary-900',
    '--theme-bg', '--theme-bg-ground', '--theme-bg-overlay',
    '--theme-border', '--theme-text', '--theme-text-secondary',
    '--primary-50', '--primary-100', '--primary-200', '--primary-300',
    '--primary-400', '--primary-500', '--primary-600', '--primary-700',
    '--primary-800', '--primary-900',
    '--primary-color', '--text-color', '--text-color-secondary',
    '--surface-0', '--surface-100', '--surface-ground',
    '--surface-section', '--surface-card', '--surface-overlay',
    '--surface-border', '--highlight-bg',
  ]
  for (const v of themeVars) {
    root.style.removeProperty(v)
  }
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) {
    meta.setAttribute('content', '#150C3A')
  }
}
